import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { SWRConfiguration } from 'swr';

import {
  disableReadOnlyFields,
  removeNavButtonSettings,
  removeSsnFieldIfManagerRestricted,
} from '~/components/Forms/helper';
import { useApplicationConfig } from '~/data/applicationConfig';
import { useEmployee } from '~/data/employee';
import { Roles } from '~/data/user';
import { useSelectedEmployeeId } from '~/hooks/employee';
import {
  useFormTemplateDetails,
  useFormTemplatesList,
  useFormTranslations,
  useFormsCustomConfig,
  useLatestTemplateVersion,
  useWorkflowConfig,
} from '~/hooks/forms';
import { useEmployeeViews } from '~/scenes/PortalViews/Views/EmployeeViews/useEmployeeViews';
import { FormioComponent, FormioFormBuilderConfig } from '~/types/forms';

export type OverlappingEmplForm = {
  empId: number;
  firstName: string;
  lastName: string;
  givenNames: string;
  ssn: string;
  birthdate: string;
  gender: string;
  profileId: number;
  employeeNumber: string;
  legalEntity: { id?: number; code?: string; name?: string };
  orgUnit?: { id?: number; number?: string; name?: string };
  positionManager: {
    id?: number;
    firstName?: string;
    lastName?: string;
    givenNames?: string;
    employeeNumber?: string;
    status?: string;
  };
  internalPosition: string;
  businessTitle?: string;
  mainCostCenter?: { id?: number; code?: string; name?: string };
  addlCostCenter?: { id?: number; code?: string; name?: string }[];
  employmentType?: { group?: string; code?: string; name?: string };
  employmentStatus?: { group?: string; code?: string; name?: string };
  employmentStartDate?: string;
  arrivalDate?: string;
  trialPeriod?: { id?: string; name?: string };
  trialPeriodEndDate?: string;
  fixedTermReason?: string;
  plannedEndDate?: string;
  employmentEndDate?: string;
  terminationDate?: string;
  terminationReason?: string;
  currency?: { group?: string; code?: string; name?: string };
  jobGrade?: { group?: string; code?: string; name?: string };
  taxNumber?: string;
  employmentInfo?: string;
  vacationBalanceCalculation?: string;
  calculateHoursBalance?: boolean;
  personnelGroup1?: { group: string; code?: string; name?: string } | null;
  personnelGroup2?: { group: string; code?: string; name?: string } | null;
  personnelGroup3?: { group: string; code?: string; name?: string } | null;
  personnelGroup4?: { group: string; code?: string; name?: string } | null;
  personnelGroup5?: { group: string; code?: string; name?: string } | null;
  phoneWork?: string;
  phoneMob?: string;
  emailWork?: string;
  office?: { id?: number; name?: string };
  officeFloor?: string;
  officeRoom?: string;
  isOverlappingEmployment: boolean;
};

export const useFormsOverlappingEmp = () => {
  const { t } = useTranslation();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const { employeeId } = useParams<{ employeeId: string }>();

  const swrConfig: SWRConfiguration = {
    revalidateOnFocus: false,
  };
  const {
    selectedEmployeeDetails: { hasRoleAccessOverEmployee },
  } = useSelectedEmployeeId();
  const userHasManagerRoleOnly = hasRoleAccessOverEmployee(
    [Roles.MANAGER],
    [Roles.HR_ADMIN, Roles.HR_STAFF],
  );

  const { applicationConfig } = useApplicationConfig();
  const managerConfig = applicationConfig.fComConfig.mgrConfig;
  const managerCanViewSsn = managerConfig.fComEnableMgrViewSsn;

  const { workflowConfig, isLoading: isWorkflowConfigLoading } =
    useWorkflowConfig({ revalidateOnFocus: false });
  const {
    employee: { empBasicData },
    isLoading: employeeLoading,
  } = useEmployee();
  const { isLoading: isEmployeeViewsLoading } = useEmployeeViews();
  const { data: formTemplates, isLoading: formTemplatesLoading } =
    useFormTemplatesList();
  const {
    data: formTemplateDetails,
    isLoading: formConfigLoading,
    mutate: refetchTemplateData,
  } = useFormTemplateDetails(selectedTemplateId, swrConfig);

  const { data: latestVersion, isLoading: latestVersionLoading } =
    useLatestTemplateVersion();
  const { data: formTranslations, isLoading: formTranslationsLoading } =
    useFormTranslations(t);

  const { applyCustomConfig, isLoading: isCustomConfigLoading } =
    useFormsCustomConfig(selectedTemplateId);

  // Memoize so the returned reference is stable across renders. existingData
  // is a useEffect dep in OverlappingEmployment.tsx and an unstable reference
  // rebuilds the Formio form on every parent re-render, wiping the user's input.
  const existingData = useMemo<Partial<OverlappingEmplForm>>(
    () => ({
      empId: +employeeId,
      ssn: empBasicData?.fEmpSocSecNum,
      firstName: empBasicData?.fEmpFirstName,
      lastName: empBasicData?.fEmpLastName,
      givenNames: empBasicData?.fEmpGivenNames,
      birthdate: empBasicData?.fEmpDateOfBirth,
      gender: empBasicData?.fEmpGender || '',
      employeeNumber: empBasicData?.fEmpPersonNumber,
      profileId: empBasicData?.fEmpProfileId,
      isOverlappingEmployment: true,
    }),
    [employeeId, empBasicData],
  );

  useEffect(() => {
    if (formTemplates?.length > 0) {
      setSelectedTemplateId(
        formTemplates?.find((pf) => pf?.settings && pf?.settings?.isDefault)
          ?._id ||
          formTemplates[0]?._id ||
          '',
      );
    }
  }, [formTemplates]);

  const formConfigData: FormioFormBuilderConfig | undefined = useMemo(() => {
    if (selectedTemplateId && formTemplateDetails) {
      const configWithReadOnlyFields =
        disableReadOnlyFields(formTemplateDetails);

      const customBottomNavButtons =
        formTemplateDetails.components?.[0]?.components?.find(
          (comp: FormioComponent) => comp.key === 'navColumnsPage1',
        );

      const updatedData = customBottomNavButtons
        ? removeNavButtonSettings(configWithReadOnlyFields)
        : configWithReadOnlyFields;

      // Remove SSN field if manager is not allowed to view Social Security Number
      const finalUpdatedData =
        userHasManagerRoleOnly && !managerCanViewSsn
          ? removeSsnFieldIfManagerRestricted(updatedData)
          : updatedData;

      return applyCustomConfig(finalUpdatedData);
    }
    return undefined;
  }, [selectedTemplateId, formTemplateDetails]);

  const isDataLoading =
    employeeLoading ||
    isEmployeeViewsLoading ||
    formTemplatesLoading ||
    latestVersionLoading ||
    formConfigLoading ||
    formTranslationsLoading ||
    isCustomConfigLoading ||
    isWorkflowConfigLoading;

  if (isDataLoading) {
    return {
      isDataLoading,
      setSelectedTemplateId,
    };
  }

  return {
    employeeId,
    formTemplates,
    selectedTemplateId,
    formConfigData,
    latestVersion,
    latestVersionLoading,
    existingData,
    formTranslations,
    isDataLoading,
    workflowConfig,
    refetchTemplateData,
    setSelectedTemplateId,
  };
};
